import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import api from "services/api";
import { usePageTitle } from "hooks/usePageTitle";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
  Stack,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import {
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import { loadExcelJS } from "helpers/loadExcelJS";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  getDateProximityColor,
  getStatusColor,
  numericComparator,
} from "helpers/ServiceHelpers";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CONST } from "constants/CONST";
import CEQuickFilter from "components/CEQuickFilter";
import DataGridResponsive from "components/DataGridResponsive";
import QuickFilterField from "components/QuickFilterField";
import { HtmlTooltip } from "components/HeatMap";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import { NAVBAR_HEIGHT } from "constants/layout";

// Batch C wave 4 — Services columns whose desktop header uses
// ColumnDateFilter (hierarchical date filter) instead of ColumnFilter. Used to
// build the card-mode filter-sheet entries with identical semantics.
const CARD_MODE_DATE_FILTER_FIELDS = ["Date", "CheckedOn"];

// Batch C wave 4 — the desktop headers of these four columns read their filter
// data from the cross-filtering `filterData` array (index = position in
// `filterColumns`); every other header passes `listOfServices` directly.
const CARD_MODE_FILTER_DATA_FIELDS = [
  "ProjectNumber",
  "Date",
  "Client",
  "Requestor",
];

// Helper function to get the appropriate status based on ServiceCategory
const getServiceStatus = (service) => {
  if (!service) return "N/A";
  const category = service.ServiceCategory;
  if (category === "Translation") {
    return service.TranslationStatus || "N/A";
  } else if (category === "Interpretation") {
    return service.InterpretationStatus || "N/A";
  } else if (category === "Other") {
    return service.OtherServiceStatus || "N/A";
  }
  return "N/A";
};

// Helper function to get the appropriate route based on ServiceCategory
const getServiceRoute = (serviceId, serviceCategory) => {
  if (serviceCategory === "Translation") {
    return `/translations/${serviceId}`;
  } else if (serviceCategory === "Interpretation") {
    return `/interpretations/${serviceId}`;
  } else if (serviceCategory === "Other") {
    return `/otherServices/${serviceId}`;
  }
  // Fallback to services route if category is unknown
  return `/services/${serviceId}`;
};

const Services = () => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault("America/New_York");

  usePageTitle("Services");
  const theme = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const [isLoading, setIsLoading] = useState(true);

  // Grid management state
  const getSavedDensity = () => {
    const saved = localStorage.getItem("SERVICES_GRID_DENSITY");
    return saved || "standard";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("SERVICES_COLUMNS_VISIBLE");
    return saved ? JSON.parse(saved) : {};
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);
  const apiRef = useGridApiRef();
  // Preserve the scroll position across refetches (saves bounce to the top)
  const { capture: captureGridScroll, restore: restoreGridScroll } =
    useDataGridScrollRestore(apiRef);

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'
  const [overlayAnchorEl, setOverlayAnchorEl] = useState(null);

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("SERVICES_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "SERVICES_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  const { setOverlayData, setOverlayName } = useStateContext();
  const { refreshPage, showSnackbar } = useStateContext();

  // Set up entity tracking for ServicesList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "ServicesList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "ServicesList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const [minMaxDates, setMinMaxDates] = useState();

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem(CONST.SERVICE_MONTHS_KEY);
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);
  const [tempSelectedMonths, setTempSelectedMonths] = useState(getSavedMonths);

  // Year selector data
  const currentYear = dayjs().year();

  // Get saved year from localStorage or use current year as default
  const getSavedYear = () => {
    const savedYear = localStorage.getItem(CONST.SERVICE_YEAR_KEY);
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  // Initialize with current year, will be updated when minMaxDates is available
  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [tempSelectedYear, setTempSelectedYear] = useState(getSavedYear);

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);

  // Generate year options when minMaxDates changes
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;

      // Create an array of years between startYear and endYear (inclusive)
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }

      setYearOptions(years);

      // If the current selected year is not in the range, update it to the current year
      // or the closest year in the range
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          // Choose the closest year in range to the current year
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }

          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]);

  const [contextMenu, setContextMenu] = useState(null);

  const [listOfServices, setListOfServices] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Derived selection for context menu actions
  const selectedService = useMemo(() => {
    return (listOfServices || []).find(({ id }) => id === selectedRow);
  }, [listOfServices, selectedRow]);

  // Selection controls
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const LONG_PRESS_MS = 500;
  const ENABLE_SELECTION = false;

  // Calendar sync progress overlay
  const [calendarSyncProgress] = useState({
    isOpen: false,
    current: 0,
    total: 0,
  });

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "SERVICES_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `SERVICES_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Remove from localStorage if no values selected
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    // Get all column field names from the columns array
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "Details",
      "ServiceStatus",
      "AccountCharge",
      "TotalCost",
      "Linguist",
      "Profit",
      "Margin",
      "ServiceType",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "Client",
      "Requestor",
      "Details",
      "ServiceStatus",
      "AccountCharge",
      "TotalCost",
      "Linguist",
      "Profit",
      "Margin",
      "ServiceType",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuAddTask = () => {
    const serviceData = listOfServices.find(({ id }) => id === selectedRow);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Service",
      },
      Service: {
        id: serviceData.id,
        ProjectNumber: serviceData.ProjectNumber,
        Details: serviceData.Details,
        ServiceCategory: serviceData.ServiceCategory,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuAddNote = () => {
    const data = {
      ServiceID: selectedRow,
      MemberID: currentUser.id,
      ModelType: "Service",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);

    setContextMenu(null);
  };

  const handleContextMenuEdit = () => {
    // Navigate to the service profile page for editing
    if (selectedRow && selectedService) {
      const route = getServiceRoute(
        selectedRow,
        selectedService.ServiceCategory,
      );
      navigate(route);
      setContextMenu(null);
    }
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    if (!ENABLE_SELECTION) return;
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    if (!ENABLE_SELECTION) return;
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (!ENABLE_SELECTION) {
      setSelectionModel([]);
      return;
    }
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      // keep last selected for highlighting
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = () => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressNextClickRef.current = false;
  };

  const isServiceChecked = useCallback((service) => {
    return !!(service?.CheckingDate || service?.CheckedBy || service?.Checker);
  }, []);

  const selectionStats = useMemo(() => {
    if (!selectionEnabled || selectionModel.length === 0) {
      return { total: 0, checked: 0, unchecked: 0, allChecked: false };
    }

    const selectedSet = new Set(selectionModel);
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedSet.has(service.id),
    );

    const checked = selectedServices.filter(isServiceChecked).length;
    const total = selectedServices.length;
    const unchecked = Math.max(total - checked, 0);

    return {
      total,
      checked,
      unchecked,
      allChecked: total > 0 && unchecked === 0,
    };
  }, [selectionEnabled, selectionModel, listOfServices, isServiceChecked]);

  // Handle date selection save (combined months and years)
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Take only the last selected year
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Save to localStorage
    localStorage.setItem(
      CONST.SERVICE_MONTHS_KEY,
      JSON.stringify(updatedSelection.months),
    );
    localStorage.setItem(
      CONST.SERVICE_YEAR_KEY,
      JSON.stringify(updatedSelection.years),
    );

    // Fetch data
    fetchServicesData(updatedSelection.months, updatedSelection.years);
  };

  const fetchServicesData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);
      captureGridScroll();

      // Ensure we have valid parameters
      let finalMonths = months;
      let finalYear = year;

      if (!finalMonths || finalMonths.length === 0) {
        finalMonths = [dayjs().format("MMM")];
        // Update both states to reflect the default value
        setSelectedMonths(finalMonths);
        setTempSelectedMonths(finalMonths);
      }

      if (!finalYear || finalYear.length === 0) {
        finalYear = [String(currentYear)];
        // Update both states to reflect the default value
        setSelectedYear(finalYear);
        setTempSelectedYear(finalYear);
      }

      // Convert months to a comma-separated string if it's an array
      const monthsParam = Array.isArray(finalMonths)
        ? finalMonths.join(",")
        : finalMonths;

      api
        .get("/services/allServices", {
          params: {
            months: monthsParam,
            year: finalYear[0], // Since year is single-select, we only need the first (and only) value
          },
        })
        .then((response) => {
          if (response !== null) {
            //REVERSE LISTS
            const services = response.data;
            services.reverse();
            setListOfServices(services);
            restoreGridScroll(services);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";
          handleErrorDialog(message, theme);

          setIsLoading(false);
        });
    },
    [
      currentYear,
      selectedMonths,
      selectedYear,
      handleErrorDialog,
      theme,
      captureGridScroll,
      restoreGridScroll,
    ],
  );

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/clients/OPIMinimums"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/services/statuses"),
        api.get("/services/services/types"),
        api.get("/dashboard/serviceDateRange"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            clientsOPIMinimumsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            minMaxDatesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              const tempLanguages = typesResponse.data.slice(0, 5);
              setListOfTypes(tempLanguages);
            }
            if (minMaxDatesResponse) {
              setMinMaxDates(minMaxDatesResponse.data);
            }
            // Mark data lists as ready
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    // Initial data fetch
    fetchServicesData();

    // Get all various data lists
    getDataLists();
  }, [refreshPage, fetchServicesData, getDataLists]); // Don't include selectedMonths or selectedYear here to avoid double fetching

  // Listen for service updates from other members
  const handleServicesListRefetch = useCallback(
    (updatedBy) => {
      // Reload the services list data
      fetchServicesData();
    },
    [fetchServicesData],
  );

  useAssignmentUpdates({
    modelType: "ServicesList",
    recordId: "all",
    onUpdate: handleServicesListRefetch,
  });

  // Sync the temporary state with the actual state when the actual state changes from outside
  useEffect(() => {
    setTempSelectedMonths(selectedMonths);
  }, [selectedMonths]);

  useEffect(() => {
    setTempSelectedYear(selectedYear);
  }, [selectedYear]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  const columns = [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 110,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      flex: 1.1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value =
          data && data !== "" ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 100,
      flex: 1.0,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: getDateProximityColor(
                theme,
                params.value,
                getServiceStatus(params.row),
              ),
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[1] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data && data.Account ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceCategory",
      headerName: "Category",
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceStatus",
      headerName: "Status",
      valueGetter: (_, params) => {
        const category = params.ServiceCategory;
        if (category === "Translation") {
          return params.TranslationStatus || "N/A";
        } else if (category === "Interpretation") {
          return params.InterpretationStatus || "N/A";
        } else if (category === "Other") {
          return params.OtherServiceStatus || "N/A";
        }
        return "N/A";
      },
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <HtmlTooltip
            title={
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {params.value}
              </Typography>
            }
          >
            <Box
              sx={{
                borderRadius: "8px",
                px: "0.5rem",
                backgroundColor: getStatusColor(theme, params.value),
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: theme.palette.background.default,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          </HtmlTooltip>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AdditionalCost",
      headerName: "Additional Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Profit",
      headerName: "Profit",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Profit;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Margin",
      headerName: "Margin",
      valueGetter: (_, params) => {
        const data = params.Margin;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const percentage = data * 1;
          if (percentage === 0) {
            value = "0%";
          } else if (percentage === 100) {
            value = "100%";
          } else {
            value = `${percentage.toFixed(2)}%`;
          }
        }

        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Cancelled",
      headerName: "Cancelled",
      valueGetter: (_, params) => {
        const data = params.Cancelled;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Cancelled;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CancellationReason",
      headerName: "Cancellation Reason",
      valueGetter: (_, params) => {
        const data = params.CancellationReason;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      minWidth: 200,
      flex: 2.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Invoiced",
      headerName: "Invoiced",
      valueGetter: (_, params) => {
        const data = params.Invoiced;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Invoiced;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Deleted",
      headerName: "Deleted",
      valueGetter: (_, params) => {
        const data = params.Deleted;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Deleted;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CreatedBy",
      headerName: "Created By",
      valueGetter: (_, params) => {
        const data = params.Creator;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedBy",
      headerName: "Checked By",
      valueGetter: (_, params) => {
        const data = params.Checker;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedOn",
      headerName: "Checked On",
      valueGetter: (_, params) => {
        const data = params.CheckingDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";

        return value;
      },
      sortComparator: dateComparator,
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.SERVICE_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.SERVICE_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const [filterModel, setFilterModel] = useState(getStoredFilterModel);
  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);
  const [sortModel, setSortModel] = useState(getStoredSortModel);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfServices || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listOfServices || listOfServices.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listOfServices, loadAllColumnFilters]); // Apply saved filters once per loaded dataset

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
    localStorage.setItem(
      CONST.SERVICE_FILTER_KEY,
      JSON.stringify({ items: [] }),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) =>
          row.ProjectNumber && row.ProjectNumber !== ""
            ? row.ProjectNumber
            : "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date && row.Date !== ""
            ? dayjs.tz(row.Date).format("M/D/YYYY")
            : "N/A",
      },
      {
        field: "Client",
        valueGetter: (row) =>
          row.Client && row.Client?.Account ? row.Client?.Account : "N/A",
      },
      {
        field: "Requestor",
        valueGetter: (row) => (row.Requestor ? row.Requestor.Name : "N/A"),
      },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "ServiceCategory",
        valueGetter: (row) => row.ServiceCategory || "N/A",
      },
      {
        field: "ServiceStatus",
        valueGetter: (row) => getServiceStatus(row),
      },
      {
        field: "AccountCharge",
        valueGetter: (row) => {
          const data = row.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      {
        field: "TotalCost",
        valueGetter: (row) => {
          const data = row.TotalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Linguist",
        valueGetter: (row) => (row.Linguist ? row.Linguist.Name : "N/A"),
      },
      {
        field: "Profit",
        valueGetter: (row) => {
          const data = row.Profit;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Margin",
        valueGetter: (row) => {
          const data = row.Margin;
          if (data !== undefined && data !== null) {
            const percentage = data * 1;
            if (percentage === 0) return "0%";
            if (percentage === 100) return "100%";
            return `${percentage.toFixed(2)}%`;
          }
          return "N/A";
        },
      },
      {
        field: "ServiceType",
        valueGetter: (row) => row.ServiceType || "N/A",
      },
      {
        field: "Cancelled",
        valueGetter: (row) => {
          const data = row.Cancelled;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CancellationReason",
        valueGetter: (row) =>
          row.CancellationReason && row.CancellationReason !== ""
            ? row.CancellationReason
            : "N/A",
      },
      {
        field: "Invoiced",
        valueGetter: (row) => {
          const data = row.Invoiced;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "Deleted",
        valueGetter: (row) => {
          const data = row.Deleted;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CreatedBy",
        valueGetter: (row) => (row.Creator ? row.Creator.Name : "N/A"),
      },
      {
        field: "CheckedBy",
        valueGetter: (row) => (row.Checker ? row.Checker.Name : "N/A"),
      },
      {
        field: "CheckedOn",
        valueGetter: (row) =>
          row.CheckingDate
            ? dayjs.tz(row.CheckingDate).format("M/D/YYYY")
            : "N/A",
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listOfServices || [];
      }

      return (listOfServices || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listOfServices, filterColumns]);

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      CONST.SERVICE_FILTER_KEY,
      JSON.stringify(newFilterModel),
    );
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(CONST.SERVICE_SORT_KEY, JSON.stringify(newSortModel));
  };

  let totalAccountCharge = 0;
  let totalTotalCost = 0;
  let totalProfit = 0;

  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));
  const includeAll = filteredRowIdsSet.size === 0;

  // Use columnFilteredData for totals calculation to include column filter effects
  const dataForTotals = columnFilteredData || [];

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    totalAccountCharge += Number(service.AccountCharge);
    totalTotalCost += Number(service.TotalCost);
    totalProfit += Number(service.Profit);
  });

  const listOfTotals = [
    { name: "Total Account Charge", total: totalAccountCharge },
    { name: "Total Total Cost", total: totalTotalCost },
    { name: "Total Profit", total: totalProfit },
  ];

  // Count statuses for Members
  let completedCount = 0;
  let confirmedCount = 0;
  let requestedCount = 0;
  let cancelledCount = 0;

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    const status = getServiceStatus(service);
    if (status === "Completed") completedCount++;
    else if (status === "Confirmed") confirmedCount++;
    else if (status === "Requested") requestedCount++;
    else if (service.Cancelled) cancelledCount++;
  });

  const listOfServiceStatuses = [
    { name: "Completed", count: completedCount },
    { name: "Confirmed", count: confirmedCount },
    { name: "Requested", count: requestedCount },
    { name: "Cancelled", count: cancelledCount },
  ];

  // Count service categories
  let totalServices = 0;
  let translationCount = 0;
  let interpretationCount = 0;
  let otherServiceCount = 0;

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    totalServices++;
    const category = service.ServiceCategory;
    if (category === "Translation") translationCount++;
    else if (category === "Interpretation") interpretationCount++;
    else if (category === "Other") otherServiceCount++;
  });

  const listOfServiceCategories = [
    { name: "Total Services", count: totalServices },
    { name: "Total Translations", count: translationCount },
    { name: "Total Interpretations", count: interpretationCount },
    { name: "Total Other Services", count: otherServiceCount },
  ];

  // State for toggling between totals, statuses, and categories
  // Check localStorage first, then fall back to role-based default
  const getInitialFooterView = () => {
    const saved = localStorage.getItem(CONST.SERVICES_FOOTER_VIEW_KEY);
    if (saved !== null) {
      return saved;
    }
    // Default: Admin sees totals, Members see statuses
    return currentUser?.role === "Admin" ? "totals" : "statuses";
  };

  const [footerView, setFooterView] = useState(getInitialFooterView);

  // Save to localStorage when toggling
  const handleToggleFooterView = () => {
    let newValue;
    if (footerView === "totals") {
      newValue = "statuses";
    } else if (footerView === "statuses") {
      newValue = "categories";
    } else {
      newValue = "totals";
    }
    setFooterView(newValue);
    localStorage.setItem(CONST.SERVICES_FOOTER_VIEW_KEY, newValue);
  };

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={!columns.some((col) => columnsVisible[col.field] === false)}
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                showSnackbar(
                  "No services data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              const csvRows = dataToExport
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            // Check if this field has a special mapping
                            const mappedPath = fieldPaths[col.field];
                            if (mappedPath) {
                              value = getFieldValue(row, mappedPath);
                            } else if (col.field === "TargetLanguage") {
                              // Special handling for TargetLanguage (complex nested array)
                              const languages = row.ServiceTargetLanguages;
                              if (languages && Array.isArray(languages)) {
                                value = languages
                                  .map((tl) => tl.Language?.Name || "")
                                  .join(" | ");
                              }
                            } else {
                              value = getFieldValue(row, col.field);
                              if (value === "" && col.field in row) {
                                value = row[col.field];
                              }
                            }
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `translations-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={async () => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No services data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              showSnackbar("Preparing XLSX export…", theme, "info");

              // Create workbook and worksheet
              const ExcelJS = await loadExcelJS();
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Translations");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              dataToExport.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      const mappedPath = fieldPaths[col.field];
                      if (mappedPath) {
                        value = getFieldValue(row, mappedPath);
                      } else if (col.field === "TargetLanguage") {
                        const languages = row.ServiceTargetLanguages;
                        if (languages && Array.isArray(languages)) {
                          value = languages
                            .map((tl) => tl.Language?.Name || "")
                            .join(" | ");
                        }
                      } else {
                        value = getFieldValue(row, col.field);
                        if (value === "" && col.field in row) {
                          value = row[col.field];
                        }
                      }
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `translations-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
              showSnackbar(
                "Couldn't prepare the XLSX export — check your connection and try again.",
                theme,
                "error",
              );
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  // Export dialog bridge — keep the dialog factory fresh at click time. The
  // export MenuItem lives inside a memoized toolbar, so it captures
  // <ExportDialogContent /> from the render where that memo last recomputed —
  // which can predate the data load (stale closure → the dialog says
  // "No data available to export"). This ref is reassigned every commit, so
  // the click always opens the latest render's dialog with current data.
  const openExportDialogRef = useRef(null);
  useEffect(() => {
    openExportDialogRef.current = () =>
      openDialog({
        title: "Export Data",
        theme,
        customContent: <ExportDialogContent />,
      });
  });

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
      selectionEnabled: toolbarSelectionEnabled,
      selectionModel: toolbarSelectionModel,
      allSelectedChecked: toolbarAllSelectedChecked,
      onCancelSelection,
      cardMode,
      quickFilterValue,
      onQuickFilterChange,
    }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const buttons = [];

      if (toolbarSelectionEnabled) {
        buttons.push({
          label: "Cancel Selection",
          onClick: () => {
            onCancelSelection();
            handleClose();
          },
          icon: null,
        });
      }

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobile && toolbarSelectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>
            </>
          )}

          {isMobile && buttons.length > 0 && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  {buttons.map((btn, index) => (
                    <MenuItem
                      key={index}
                      onClick={btn.onClick}
                      disabled={btn.disabled}
                      sx={{
                        color: btn.isChecked
                          ? theme.palette.text.disabled
                          : theme.palette.primary.main,
                      }}
                    >
                      {btn.icon && (
                        <ListItemIcon
                          sx={{
                            color: btn.isChecked
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                          }}
                        >
                          {btn.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText>{btn.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Box>
              </Popover>
            </>
          )}

          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.alt,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobile && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            {cardMode ? (
              <QuickFilterField
                iconOnly
                value={quickFilterValue}
                onChange={onQuickFilterChange}
              />
            ) : (
              <CEQuickFilter iconOnly={isMobile} />
            )}

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: toolbarTheme.palette.grey.main,
                  borderColor: toolbarTheme.palette.divider,
                  border: `1px solid ${toolbarTheme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: toolbarTheme.palette.primary.main,
                    //backgroundColor: toolbarTheme.palette.primary.buttonHoverSelected,
                    color: toolbarTheme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />
              
              <MenuItem
                onClick={() => {
                  openExportDialogRef.current?.();
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme, isMobile]);

  // Batch C wave 4 — hoisted out of customFooter so the SAME element tree
  // renders in both the desktop grid footer and card mode (mobile.footer).
  const totalsStrip = (
    <Box
      sx={{
        p: "1rem",
        display: "flex",
        flexWrap: "wrap", // wrap to new line if too long
        gap: 2, // spacing between items
        alignItems: "center",
      }}
    >
      <IconButton
        onClick={handleToggleFooterView}
        size="small"
        sx={{
          color: theme.palette.primary.main,
          "&:hover": {
            color: theme.palette.primary.buttonHoverSelected,
          },
        }}
      >
        <SwapHorizRoundedIcon fontSize="small" />
      </IconButton>

      {footerView === "totals"
        ? listOfTotals.map((total) => {
            return (
              <Box key={`${total}-${total.name}`}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {total.name}:
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme?.palette?.secondary?.[400],
                      fontWeight: 600,
                    }}
                  >
                    ${Number(total.total).toLocaleString()}
                  </Typography>
                </Stack>
              </Box>
            );
          })
        : footerView === "statuses"
          ? listOfServiceStatuses.map((status) => {
              return (
                <Box key={`${status}-${status.name}`}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flexWrap: "wrap", justifyContent: "center" }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {status.name}:
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme?.palette?.secondary?.[400],
                        fontWeight: 600,
                      }}
                    >
                      {status.count}
                    </Typography>
                  </Stack>
                </Box>
              );
            })
          : listOfServiceCategories.map((category) => {
              return (
                <Box key={`${category}-${category.name}`}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flexWrap: "wrap", justifyContent: "center" }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {category.name}:
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: theme?.palette?.secondary?.[400],
                        fontWeight: 600,
                      }}
                    >
                      {category.count}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
    </Box>
  );

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        {totalsStrip}

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  // ── Batch C wave 4 — card-mode filter sheet ────────────────────────────────
  // One entry per visible column, built from the exact same props as its
  // desktop header (DataGridHeader → ColumnFilter / ColumnDateFilter) so filter
  // semantics are identical between modes. In card mode the desktop headers are
  // not rendered, so these sheet copies are the only live filter instances.
  const buildMobileFilterNode = (field) => {
    const colDef = filteredColumns.find((col) => col.field === field);
    const dataIndex = filterColumns.findIndex((col) => col.field === field);
    const FilterComponent = CARD_MODE_DATE_FILTER_FIELDS.includes(field)
      ? ColumnDateFilter
      : ColumnFilter;
    return (
      // The header filters keep their trigger at opacity 0 until the column
      // header is hovered — impossible inside a touch sheet, so the sheet copy
      // forces the trigger visible (the desktop headers are untouched).
      <Box sx={{ "& .MuiIconButton-root": { opacity: 1 } }}>
        <FilterComponent
          data={
            CARD_MODE_FILTER_DATA_FIELDS.includes(field)
              ? filterData[dataIndex] || []
              : listOfServices || []
          }
          field={field}
          valueGetter={colDef?.valueGetter}
          selectedValues={columnFilters[field] || []}
          onFilterChange={(values) =>
            handleColumnFilterChangeWithStorage(field, values)
          }
          onSortChange={(sortField, direction) => {
            const newSortModel = direction
              ? [{ field: sortField, sort: direction }]
              : [];
            setSortModel(newSortModel);
            handleSortModelChange(newSortModel);
          }}
          currentSort={sortModel.find((s) => s.field === field)?.sort || null}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
      </Box>
    );
  };

  const mobileFilterEntries = filteredColumns.map((col) => {
    const selected = columnFilters[col.field] || [];
    return {
      field: col.field,
      headerName: col.headerName || col.field,
      summary: selected.length > 0 ? `${selected.length} selected` : "All",
      active: selected.length > 0,
      node: buildMobileFilterNode(col.field),
    };
  });

  const mobileActiveFilterCount = mobileFilterEntries.filter(
    (entry) => entry.active,
  ).length;

  // Chips row (§3b): one removable chip per active column filter, plus a
  // trailing "Clear all" chip when 2+ filters are active (same semantics as
  // the sheet / toolbar clear). The wrapper adds the search chip itself.
  const mobileFilterChips = mobileFilterEntries
    .filter((entry) => entry.active)
    .map((entry) => ({
      key: `column-filter-${entry.field}`,
      label: `${entry.headerName}: ${columnFilters[entry.field].length} selected`,
      onClear: () => handleColumnFilterChangeWithStorage(entry.field, []),
    }));

  if (mobileFilterChips.length >= 2) {
    mobileFilterChips.push({
      key: "column-filters-clear-all",
      label: "Clear all",
      onClear: () => clearAllFiltersRef.current(),
    });
  }

  return (
    <Box
      //borderRadius="1.5rem 1.5rem 0 0"
      p="1rem 1rem 0 1rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`, // Adjust to take full viewport height minus margin
        // 100dvh where supported: bottom controls stay clear of iOS Safari's collapsing chrome
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Calendar Sync Progress Overlay */}
      {calendarSyncProgress.isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            backgroundColor: theme.palette.background.paper,
            borderRadius: "8px",
            p: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            minWidth: "300px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Adding to Calendar
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: theme.palette.text.secondary }}
          >
            {calendarSyncProgress.current} / {calendarSyncProgress.total}
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: "8px",
              backgroundColor: theme.palette.action.disabled,
              borderRadius: "4px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                backgroundColor: theme.palette.primary.main,
                width: `${(calendarSyncProgress.current / calendarSyncProgress.total) * 100}%`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Overlay Backdrop */}
      {calendarSyncProgress.isOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 9998,
          }}
        />
      )}

      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid size={{ xs: 12 }} sx={{ px: 1 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  flex: isMobile ? 0 : 1,
                  maxWidth: isMobile ? undefined : FILTER_SELECTOR_WIDTH,
                }}
              >
                <CustomDateSelector
                  label="Date Range"
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={tempSelectedMonths}
                  selectedYears={tempSelectedYear}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={false}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGridResponsive
          localeText={{
            noRowsLabel: "No service projects added yet for this month.",
            noResultsOverlayLabel: "No matches for your filters",
          }}
          loading={isLoading}
          mobile={{
            // Batch C wave 4 — card mode (mobile) config. The desktop grid
            // ignores this prop (the wrapper drops it before rendering).
            // ProjectNumber is the field managers use to reference a service
            // project (unique id, first column); Status carries the tinted
            // status chip from its own renderCell.
            primaryField: "ProjectNumber",
            badgeField: "ServiceStatus",
            // §8 audit: no action columns, no interactive cells.
            hiddenFields: [],
            // Long free-text columns never read as a single line.
            multilineFields: ["Details", "CancellationReason"],
            pageSize: 20,
            // Services has no selection flow (ENABLE_SELECTION = false).
            selectable: false,
            // 2026-10-05 (Amine): mobile has no right-click — the card's
            // top-right ⋮ replays this screen's context menu (same handler,
            // same items and disabled states).
            contextMenuButton: true,
            // Card-mode filter sheet: one entry per visible column, reusing
            // the exact header ColumnFilter / ColumnDateFilter props;
            // activeCount drives the toolbar badge; Clear all reuses the
            // toolbar's own clear (column filters + storage + quick filter).
            filters: mobileFilterEntries,
            activeCount: mobileActiveFilterCount,
            onClearFilters: () => clearAllFiltersRef.current(),
            // The same element tree as the desktop grid footer (hoisted above
            // into `totalsStrip`).
            footer: totalsStrip,
            // Removable chips under the control row (the wrapper adds the
            // search chip itself).
            chips: mobileFilterChips,
          }}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          checkboxSelection={ENABLE_SELECTION && selectionEnabled}
          rowSelectionModel={ENABLE_SELECTION ? selectionModel : []}
          onRowSelectionModelChange={
            ENABLE_SELECTION ? handleRowSelectionModelChange : undefined
          }
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          disableColumnSorting
          onStateChange={(e) => {
            const lookup = e.filter?.filteredRowsLookup;
            if (!lookup) return;

            const nextIds = Object.keys(lookup).filter((key) => lookup[key]);
            nextIds.sort();

            const prevIds = prevFilteredRowIdsRef.current;
            const isSame =
              prevIds.length === nextIds.length &&
              prevIds.every((id, idx) => id === nextIds[idx]);

            if (!isSame) {
              prevFilteredRowIdsRef.current = nextIds;
              setFilterRowIds(nextIds);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          apiRef={apiRef}
          density={density}
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            if (ENABLE_SELECTION && selectionEnabled) {
              if (suppressNextClickRef.current) {
                event.defaultMuiPrevented = true;
                suppressNextClickRef.current = false;
              }
              return;
            }

            // Batch C wave 4 + 2026-10-05 (Amine) — Services cards keep the
            // F1 model, now the blanket rule on every card screen: a single
            // tap opens the service detail exactly like the desktop
            // double-click. This screen has no selection flow
            // (ENABLE_SELECTION = false), so there is no selection-armed
            // branch to divert the tap; keeping single-tap-open commits every
            // tap to opening the profile - collapsed OR expanded, since
            // the wrapper renders the expanded fields inside the CardActionArea
            // (2026-10-05 fix). Only the "Show N more" strip toggles instead
            // of opening. No suppressNextClickRef guard is needed here:
            // selection is disabled on this screen (ENABLE_SELECTION = false),
            // so the long-press handlers are never attached and the ref can
            // never be armed.
            if (isMobile) {
              const data = {
                Service: params.row,
                ServiceID: params.row.id,

                Clients: listOfClients,
                Linguists: listOfLinguists,
                Languages: listOfLanguages,
                Services: listOfServices,
                ServiceStatuses: listOfStatuses,
                ServiceTypes: listOfTypes,

                ComingFrom: `/services`,
              };

              const route = getServiceRoute(
                params.row.id,
                params.row.ServiceCategory,
              );
              navigate(route, { state: data });
              return;
            }

            setSelectionModel([params.id]);
          }}
          onRowDoubleClick={(params) => {
            if (openFilter || (ENABLE_SELECTION && selectionEnabled)) return;

            const data = {
              Service: params.row,
              ServiceID: params.row.id,

              Clients: listOfClients,
              Linguists: listOfLinguists,
              Languages: listOfLanguages,
              Services: listOfServices,
              ServiceStatuses: listOfStatuses,
              ServiceTypes: listOfTypes,

              ComingFrom: `/services`,
            };

            const route = getServiceRoute(
              params.row.id,
              params.row.ServiceCategory,
            );
            navigate(route, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "No" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar, footer: customFooter }}
          columns={filteredColumns}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: ENABLE_SELECTION ? handleRowMouseDown : undefined,
              onMouseUp: ENABLE_SELECTION ? handleRowMouseUp : undefined,
              onMouseLeave: ENABLE_SELECTION ? handleRowMouseUp : undefined,
              onTouchStart: ENABLE_SELECTION ? handleRowTouchStart : undefined,
              onTouchEnd: ENABLE_SELECTION ? handleRowTouchEnd : undefined,
              onTouchCancel: ENABLE_SELECTION ? handleRowTouchEnd : undefined,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: () => clearAllFiltersRef.current(),
              theme,
              selectionEnabled: ENABLE_SELECTION && selectionEnabled,
              selectionModel: ENABLE_SELECTION ? selectionModel : [],
              allSelectedChecked: ENABLE_SELECTION
                ? selectionStats.allChecked
                : false,
              onCancelSelection: handleCancelSelection,
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            height: "100%", // Make DataGrid take full height of container
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />

        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Edit
            </ListItemText>
          </MenuItem>

          <MenuItem onClick={handleContextMenuAddTask}>
            <ListItemIcon>
              <AddTaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Add Task
            </ListItemText>
          </MenuItem>

          <MenuItem onClick={handleContextMenuAddNote}>
            <ListItemIcon>
              <CommentRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Add Note
            </ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={
            overlayAnchorEl
              ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
              : undefined
          }
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              maxWidth: "calc(100vw - 2rem)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Services;
